import { useCallback, useMemo } from 'react';

import { useMaybeTeacherContext } from 'ms-pages/Teacher/TeacherContext/useTeacherContext';
import type {
  ClassSelectionPayload,
  StudentSelectionPayload,
} from 'ms-pages/Teacher/components/ClassAndStudentSelector';
import { useQueryParamFlag } from 'ms-utils/hooks/useQueryParam';
import extractNode from 'ms-utils/relay/extractNode';

type State = {
  taskName: string;
  taskStartDate: Date;
  taskDueDate: Date;
  selectedClasses: readonly ClassSelectionPayload[];
  selectedStudents: readonly StudentSelectionPayload[];
};
export const isTaskCreationFormValid = (state: State): boolean =>
  state.taskName !== '' &&
  state.taskStartDate < state.taskDueDate &&
  (state.selectedClasses.length > 0 || state.selectedStudents.length > 0);

export type ClassAsIds = {
  classId: string;
  studentIds: string[];
};
export const parseCompleteAndPartialClasses = (
  classes: readonly ClassSelectionPayload[],
  excludedStudents: readonly StudentSelectionPayload[],
) => {
  const classesAsIds: Array<ClassAsIds> = classes.map(c => ({
    classId: c.id,
    studentIds: c.students?.edges.map(({ node }) => node.id) ?? [],
  }));
  const completeClassIds = classesAsIds
    .filter(({ studentIds }) => {
      return !excludedStudents.some(excludedStudent =>
        studentIds.includes(excludedStudent.id),
      );
    })
    .map<string>(({ classId }) => classId);
  const partialClasses = classesAsIds
    .filter(({ studentIds }) => {
      return excludedStudents.some(excludedStudent =>
        studentIds.includes(excludedStudent.id),
      );
    })
    .map<ClassAsIds>(({ classId, studentIds }) => ({
      classId,
      studentIds: studentIds.filter(
        studentId => !excludedStudents.map(s => s.id).includes(studentId),
      ),
    }));
  return { completeClassIds, partialClasses };
};

// From https://github.com/mathspace/mathspace/blob/master/msproblem/content/interaction/tasks/custom_tasks/models.py
// NB: this is an override for custom tasks only, while the base class has a max lenght of 255; But: the safer, the better
export const TASK_TITLE_MAX_LENGTH = 128;

// From https://github.com/mathspace/mathspace/blob/master/msproblem/content/interaction/task_template/models.py
export const TASK_TEMPLATE_TITLE_MAX_LENGTH = 255;

/** Begin experimental code for userpilot flows */
export function useIsFirstTaskBeingCreated() {
  // We want to know if this is the first time the teacher has created a task,
  // in that case, we will append a `first-task-created=true` to the URL search string

  // Use useMaybeTeacherContext because an invited teacher doesn't have any classes and `useTeacherContext` will throw an error if trying to create a task before the teacher has created any classes.
  const { schoolList } = useMaybeTeacherContext();
  const classList = useMemo(
    () =>
      schoolList.flatMap(school =>
        school.classes == null ? [] : extractNode(school.classes),
      ),
    [schoolList],
  );

  // NB: teacher context doesn't get updated when the teacher creates a task,
  // so `isFirstTaskBeingCreated` could be incorrectly stay true if teachers creates other tasks in the same session.
  // Although, since this is marked specifically as an experimental feature for userpilot, and since userpilot flows will be shown only once, we can be a bit lenient here.
  const isFirstTaskBeingCreated = useMemo(
    () => classList.every(klass => klass.hasTasks === false),
    [classList],
  );
  const isFirstTaskQueryParam = useQueryParamFlag('first-task-created');

  const setIsFirstTaskQueryParam = useCallback(() => {
    isFirstTaskQueryParam.on();
  }, [isFirstTaskQueryParam]);

  return [isFirstTaskBeingCreated, setIsFirstTaskQueryParam] as const;
}
/** End experimental code for userpilot flows */
